import type { Money } from "./types";

const digitsByCurrency = new Map<string, number>();

/** How many decimal places the currency's major unit has: 2 for INR and USD, 0 for JPY, 3 for KWD. */
export function minorUnitDigits(currency: string): number {
  const known = digitsByCurrency.get(currency);
  if (known !== undefined) return known;
  const digits = new Intl.NumberFormat("en", { style: "currency", currency }).resolvedOptions().maximumFractionDigits ?? 2;
  digitsByCurrency.set(currency, digits);
  return digits;
}

/** The amount in major units (rupees), for display only. Do the maths on `amountMinor`. */
export function toMajor(money: Money): number {
  return money.amountMinor / 10 ** minorUnitDigits(money.currency);
}

/**
 * Money from a major-unit amount (₹1,243.50 -> 124350 paise), rounded to the nearest minor unit.
 * The product is trimmed to 15 significant digits first so 1.005 rupees is 101 paise, not 100.
 */
export function fromMajor(amount: number, currency: string): Money {
  const exact = Number((amount * 10 ** minorUnitDigits(currency)).toPrecision(15));
  return { amountMinor: roundHalfAwayFromZero(exact), currency };
}

function assertSameCurrency(a: Money, b: Money): void {
  if (a.currency !== b.currency) throw new Error(`Cannot combine ${a.currency} with ${b.currency}`);
}

export function addMoney(a: Money, b: Money): Money {
  assertSameCurrency(a, b);
  return { amountMinor: a.amountMinor + b.amountMinor, currency: a.currency };
}

export function subtractMoney(a: Money, b: Money): Money {
  assertSameCurrency(a, b);
  return { amountMinor: a.amountMinor - b.amountMinor, currency: a.currency };
}

export function negateMoney(money: Money): Money {
  return { amountMinor: -money.amountMinor, currency: money.currency };
}

export function sumMoney(values: readonly Money[], currency: string): Money {
  return values.reduce(addMoney, { amountMinor: 0, currency });
}

/** Rounds half away from zero, so -2.5 becomes -3 like the till does. */
function roundHalfAwayFromZero(value: number): number {
  return Math.sign(value) * Math.round(Math.abs(value));
}

/** `money` times `factor` (a rate such as 0.18), rounded to the nearest minor unit. */
export function scaleMoney(money: Money, factor: number): Money {
  return { amountMinor: roundHalfAwayFromZero(money.amountMinor * factor), currency: money.currency };
}

/** `percent` of the amount (18 means 18%), rounded to the nearest minor unit. */
export function percentOfMoney(money: Money, percent: number): Money {
  return scaleMoney(money, percent / 100);
}

export function isZeroMoney(money: Money): boolean {
  return money.amountMinor === 0;
}
